import { useLayoutEffect } from "react";
import { alpha, useTheme } from "@mui/material/styles";

/**
 * Publishes the active theme to the document, for the three consumers the MUI
 * tree cannot reach:
 *
 * - **`data-ess-mode` on `<html>`** — the hook a plain stylesheet scopes a
 *   mode-specific rule to, when a CSS var is not enough (a whole rule
 *   appearing or disappearing rather than one value changing).
 * - **`--ess-*` custom properties** — the tokens `src/tour/tour.css` needs.
 *   driver.js's popover is portalled outside the MUI tree, so it is painted by
 *   a stylesheet, and `RavenThemeCssVars` only mirrors the palette's *surfaces*
 *   (`--raven-bg-page`, `--raven-bg-paper`, `--raven-border-brand`) — not its
 *   text or brand tiers. Publishing them here is what keeps one stylesheet
 *   correct in both modes instead of a duplicated dark block that has to be
 *   kept in sync with the palette by hand.
 * - **`color-scheme` on `<html>` + the `theme-color` meta** — the browser's own
 *   chrome: native scrollbars, form-control defaults, the mobile address bar.
 *   Without `color-scheme` the scrollbar stays light-on-light over a near-black
 *   page.
 *
 * Renders nothing. Mounted inside the appearance provider, and again inside
 * `ForceLightTheme` so a pinned-light subtree (the print routes) publishes
 * light too. Each instance restores what it overwrote when it unmounts or
 * re-runs, so leaving a print route hands the document back to the app's
 * mode.
 */
export default function ColorSchemeSync(): null {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const barColor = theme.palette.background.paper;

  useLayoutEffect(() => {
    const root = document.documentElement;
    const prevMode = root.dataset.essMode;
    const prevScheme = root.style.colorScheme;
    root.dataset.essMode = mode;
    root.style.colorScheme = mode;

    const { palette } = theme;
    // The CTA tier: `primary.dark` in light (the #B35300 fill), the lifted
    // `primary.main` in dark — the light fill is unreadable on a dark page and
    // the dark tier needs the near-black label, exactly as MuiButton does.
    const ctaBg = mode === "dark" ? palette.primary.main : palette.primary.dark;
    const vars: Record<string, string> = {
      "--ess-text-primary": palette.text.primary,
      "--ess-text-secondary": palette.text.secondary,
      "--ess-brand": palette.primary.main,
      "--ess-brand-rim": alpha(palette.primary.main, 0.5),
      "--ess-brand-wash": alpha(palette.primary.main, mode === "dark" ? 0.12 : 0.06),
      "--ess-cta-bg": ctaBg,
      "--ess-cta-bg-hover":
        mode === "dark"
          ? palette.primary.light
          : (palette.navActive ?? palette.primary.dark),
      "--ess-cta-fg": palette.primary.contrastText,
      // Light mode floats a surface on a soft warm spread; dark mode lifts it
      // to `background.paper` and lets a single contact layer do the rest.
      "--ess-float-bg":
        mode === "dark" ? palette.background.paper : palette.background.default,
      "--ess-float-shadow":
        mode === "dark"
          ? "0 2px 10px rgba(0,0,0,0.55)"
          : "0 1px 0 rgba(14,13,16,0.04), 0 18px 32px -20px rgba(14,13,16,0.18), 0 8px 18px -10px rgba(194,65,12,0.18)",
    };
    const prevVars = Object.keys(vars).map(
      (k) => [k, root.style.getPropertyValue(k)] as const,
    );
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]:not([media])',
    );
    const previous = meta?.getAttribute("content") ?? null;
    if (meta) meta.setAttribute("content", barColor);

    return () => {
      if (meta && previous !== null) meta.setAttribute("content", previous);
      if (prevMode === undefined) delete root.dataset.essMode;
      else root.dataset.essMode = prevMode;
      root.style.colorScheme = prevScheme;
      prevVars.forEach(([k, v]) =>
        v ? root.style.setProperty(k, v) : root.style.removeProperty(k),
      );
    };
  }, [theme, mode, barColor]);

  return null;
}
